/* ============================================================================
 * CONNEC Outdoors — Masquer les images des couleurs dépubliées (page produit)
 * ----------------------------------------------------------------------------
 * CONTEXTE
 *   Sur les pages produit (thème Impact, NON gérées par Boost), dépublier une
 *   variante de couleur masque l'option mais laisse ses images dans la galerie.
 *   On s'appuie sur le CODE COULEUR présent à la fois dans le SKU de variante et
 *   dans le nom de fichier des images.
 *
 * CONVENTION (déjà en place chez CONNEC)
 *   SKU variante  :  <style>-<codeCouleur>-<taille>   ex. 2023084-166-M
 *   Nom de fichier:  <style>-<codeCouleur>-<index>_NOM_...   ex. 2023084-166-1_MCKENZIE_HOODIE.jpg
 *   -> le code couleur est le 2e segment séparé par "-".
 *
 * FONCTIONNEMENT
 *   1) Lit les SKU des variantes PUBLIÉES dans /products/{handle}.js
 *      -> ensemble des codes couleur publiés.
 *   2) Pour chaque média de la galerie, lit le code couleur de son nom de fichier.
 *   3) Masque le média si son code couleur n'est PAS publié.
 *   Se ré-applique aux changements de galerie (sélection couleur, carousel) via
 *   un MutationObserver. Aucune saisie manuelle requise.
 *
 * SÉCURITÉ
 *   - Une image n'est filtrée QUE si son nom de fichier commence par le bon code
 *     de style (1er segment du SKU, ex. 2023084). Tout fichier hors convention
 *     (lifestyle, ancien nommage) est TOUJOURS laissé visible.
 *   - Si aucun code couleur publié n'est détecté, le script ne touche à rien.
 *   - Une image ATTACHÉE à une variante publiée (son featured_media) n'est JAMAIS
 *     masquée, quel que soit le nom du fichier, et son code couleur est dès lors
 *     tenu pour publié — ce qui préserve aussi les autres photos de la couleur.
 *     Sans ce garde-fou, un fichier hors convention (3014012-whisky-1 au lieu de
 *     3014012-594-1) faisait disparaître l'image de la couleur sélectionnée ;
 *     combiné au filtre par couleur de connec-variant-gallery-filter.js, ça
 *     vidait complètement la galerie du produit.
 *   - La comparaison des codes couleur ignore la casse (« 6g6 » == « 6G6 »).
 *
 * INSTALLATION (thème, PAS Boost)
 *   Shopify Admin > Online Store > Themes > Edit code :
 *   1) Assets > Add a new asset > connec-hide-unpublished-media.js (ce contenu).
 *   2) Dans theme.liquid, juste avant </body> :
 *        <script src="{{ 'connec-hide-unpublished-media.js' | asset_url }}" defer></script>
 * ========================================================================== */

(function () {
  'use strict';

  var SKU_COLOR_INDEX  = 1; // segment du code couleur dans le SKU (séparé par "-")
  var FILE_COLOR_INDEX = 1; // segment du code couleur dans le nom de fichier

  var mediaInfo = null;            // { mediaId -> { style, color } | null }
  var publishedColorCodes = null;  // { codeCouleur -> true }
  var styleCodes = null;           // { codeStyle -> true }  (1er segment du SKU)
  var protectedIds = null;         // { mediaId  -> true }  médias d'une variante publiée
  var protectedFiles = null;       // { basename -> true }  idem, pour les vignettes
  var ready = false;

  function handle() {
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  // Codes couleur/style comparés sans tenir compte de la casse
  function norm(v) {
    return String(v == null ? '' : v).trim().toUpperCase();
  }

  // Nom de fichier seul, sans querystring : ".../3014012-594-1_HAT.png?v=1" -> "3014012-594-1_HAT.png"
  function basename(src) {
    if (!src) return '';
    return src.split('?')[0].split('/').pop();
  }

  // Découpe un SKU "2023084-166-M" -> { style:"2023084", color:"166" }
  function partsFromSku(sku) {
    if (!sku) return null;
    var parts = sku.split('-');
    if (parts.length <= SKU_COLOR_INDEX) return null;
    return { style: norm(parts[0]), color: norm(parts[SKU_COLOR_INDEX]) };
  }

  // Découpe un nom de fichier "2023084-166-1_MCKENZIE.jpg" -> { style, color }
  // Exige le format <style>-<couleur>-<index> (au moins 3 segments).
  function partsFromSrc(src) {
    if (!src) return null;
    var file = basename(src);       // nom de fichier sans querystring
    var token = file.split('_')[0]; // "2023084-166-1"
    var parts = token.split('-');
    if (parts.length < 3) return null;
    var style = norm(parts[0]);
    var color = norm(parts[FILE_COLOR_INDEX]);
    if (!/^\w+$/.test(style) || !/^\w+$/.test(color)) return null;
    return { style: style, color: color };
  }

  // Charge une fois : code couleur par média + codes couleur publiés
  function loadData(cb) {
    if (ready) { cb(); return; }
    var h = handle();
    if (!h) { cb(); return; }

    fetch('/products/' + h + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        mediaInfo = {};
        publishedColorCodes = {};
        styleCodes = {};
        protectedIds = {};
        protectedFiles = {};
        if (p) {
          (p.variants || []).forEach(function (v) {
            var sk = partsFromSku(v.sku);
            if (sk) { publishedColorCodes[sk.color] = true; styleCodes[sk.style] = true; }

            // GARDE-FOU : l'image attachée à cette variante publiée reste visible,
            // même si son nom de fichier ne suit pas la convention de nommage.
            var fm = v.featured_media || v.featured_image;
            if (!fm) return;
            if (fm.id != null) protectedIds[String(fm.id)] = true;
            var file = basename(fm.src || (fm.preview_image && fm.preview_image.src) || '');
            if (file) protectedFiles[file] = true;
          });
          // Un fichier protégé prouve que SON code couleur est publié, même s'il ne
          // correspond à aucun SKU : on l'ajoute aux codes publiés pour que les autres
          // photos de la même couleur (-02, -03 ...) restent visibles elles aussi.
          Object.keys(protectedFiles).forEach(function (file) {
            var info = partsFromSrc(file);
            if (info && styleCodes[info.style]) publishedColorCodes[info.color] = true;
          });

          (p.media || []).forEach(function (m) {
            var src = m.src || (m.preview_image && m.preview_image.src) || '';
            mediaInfo[String(m.id)] = partsFromSrc(src);
          });
        }
        ready = true;
        cb();
      })
      .catch(function () { ready = true; cb(); });
  }

  // Décide si un { style, color } doit être masqué (true) ou gardé (false/null)
  // Retourne null si non concerné (hors convention / autre style).
  function shouldHide(info) {
    if (!info || !styleCodes[info.style]) return null;
    return !publishedColorCodes[info.color];
  }

  // Remonte de l'image vers son conteneur de vignette/slide à masquer
  function thumbContainer(img) {
    var c = img.closest(
      '[class*="thumbnail"], [class*="thumb"], .product-gallery__media, li, button, [role="listitem"]'
    );
    return c || img;
  }

  function srcOf(img) {
    return img.currentSrc || img.src ||
      img.getAttribute('data-src') ||
      (img.getAttribute('data-srcset') || '').split(' ')[0] || '';
  }

  function apply() {
    if (!ready || !mediaInfo || !publishedColorCodes) return;
    if (Object.keys(publishedColorCodes).length === 0) return; // sécurité

    // ── Passe 1 : grandes images du carousel (data-media-id + JSON) ──
    var nodes = document.querySelectorAll('[data-media-id]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var id = el.getAttribute('data-media-id');
      if (!id) continue;
      if (protectedIds[id]) { restore(el); continue; }
      var verdict = shouldHide(mediaInfo[id]);
      if (verdict === null) { restore(el); continue; }
      if (verdict) hide(el); else restore(el);
    }

    // ── Passe 2 : vignettes & autres images, via le nom de fichier ──
    var root = document.querySelector('product-gallery, [class*="product-gallery"]') || document;
    var imgs = root.querySelectorAll('img');
    for (var j = 0; j < imgs.length; j++) {
      var img = imgs[j];
      var src = srcOf(img);
      var target = thumbContainer(img);
      if (protectedFiles[basename(src)]) { restore(target); continue; }
      var v = shouldHide(partsFromSrc(src));
      if (v === null) continue;            // hors convention -> on ne touche pas
      if (v) hide(target); else restore(target);
    }
  }

  // Masquage par RÈGLE CSS (et non style inline) : la galerie Maestrooo réécrit
  // les styles inline des médias à chaque rendu, ce qui effaçait un display:none
  // inline. Une règle CSS ciblant le marqueur résiste à ces réécritures.
  function injectCSS() {
    if (document.getElementById('connec-hide-media-css')) return;
    var s = document.createElement('style');
    s.id = 'connec-hide-media-css';
    s.textContent =
      '[data-connec-hidden-media="1"],' +
      '.product-gallery__media[data-connec-hidden-media="1"],' +
      'img[data-connec-hidden-media="1"]{display:none !important;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function hide(el) {
    if (el.getAttribute('data-connec-hidden-media') !== '1') {
      el.setAttribute('data-connec-hidden-media', '1');
    }
  }

  function restore(el) {
    if (el.getAttribute('data-connec-hidden-media') === '1') {
      el.removeAttribute('data-connec-hidden-media');
    }
  }

  // ── Démarrage + ré-application sur changements de galerie ──
  function start() {
    injectCSS();
    loadData(function () {
      apply();

      var gallery = document.querySelector('product-gallery, [class*="product-gallery"]') || document.body;
      var pending = false;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () { pending = false; apply(); }, 50);
      });
      observer.observe(gallery, {
        childList: true, subtree: true,
        attributes: true, attributeFilter: ['data-media-id', 'class']
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
